<template>
  <div class="quote-form">
    <Spin fix v-if="loading"></Spin>
    <Form v-else ref="quoteForm" :model="formData" label-position="top" :disabled="disabled">
      <Row :gutter="32" class="align-items-center">
        <Col span="24">
          <FormItem :label="$t('quote.info.title')" prop="title" :rules="{ required: true, message: $t('quote.title_is_required') }">
            <Input v-model="formData.title"></Input>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="24">
          <FormItem :label="$t('quote.info.basecamp_todo_link')" prop="basecamp_todo_link" :rules="{ trigger: 'blur', validator: checkBasecampProjectUrl }">
            <Input v-model="formData.basecamp_todo_link"
                   placeholder="For example, https://3.basecamp.com/3679140/buckets/8603668/todos/5276662841"></Input>
            <div class="text-align-right" style="color: rgb(128, 134, 149); font-size: 12px;">{{ $t('quote.basecamp_todo_link_desc') }}</div>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="6">
          <FormItem :label="$t('quote.info.method')" prop="method" :rules="{ required: true, message: $t('quote.method_is_required') }">
            <QuoteMethodSelect v-model="formData.method" :placeholder="$t('quote.select_quote_method')"></QuoteMethodSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('quote.info.type')" prop="type" :rules="{ required: true, message: $t('quote.type_is_required') }">
            <Select v-model="formData.type" @on-change="handleQuoteTypeChanged" :disabled="!!formData.id">
              <Option value="single_product">{{ $t('quote.types.single_product') }}</Option>
              <Option value="service">{{ $t('quote.types.service') }}</Option>
              <Option value="order_change">{{ $t('quote.types.order_change') }}</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('menu.quote.estimate_number')" prop="number">
            <Input v-model="formData.number" />
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('quote.info.expires_date')" prop="expires_date" :rules="{ required: true, message: $t('quote.expires_date_is_required') }">
            <DatePicker v-model="formData.expires_date" @on-change="handleExpiresDateChange" type="date" format="yyyy-MM-dd"></DatePicker>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('quote.info.customer')" prop="customer_id" :rules="{ required: true, message: $t('quote.customer_is_required') }">
            <CustomerInfo v-model="formData.customer_id" v-bind:account.sync="formData.customer"></CustomerInfo>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem v-if="formData.type === 'single_product'" :label="$t('quote.info.nsf_phase')" prop="nsf_phase" :rules="{ required: formData.type !== 'service', message: this.$t('quote.nsf_phase_is_required') }">
            <NsfPhaseSelect v-model="formData.nsf_phase" :disabled="formData.type === 'service'"></NsfPhaseSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem v-if="formData.type === 'single_product'" :label="$t('quote.info.process_order_category')" prop="process_order_category" :rules="{ required: formData.type !== 'service', message: this.$t('quote.process_order_category_is_required') }">
            <ProcessOrderCategorySelect v-model="formData.process_order_category" :disabled="formData.type === 'service'"></ProcessOrderCategorySelect>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" class="align-items-center" v-show="formData.type">
        <Col span="24">
          <QuoteTypeSingleProduct ref="singleProductRef"
                                  v-if="formData.type === 'single_product'"
                                  v-model="formData.product_id"
                                  :singleProduct.sync="formData.data"
                                  :has-cost-estimated="hasCostEstimated"
                                  :disabled="formData.type === 'service' || disabled"
                                  :quote="quote">
          </QuoteTypeSingleProduct>
        </Col>
      </Row>
      <Row :gutter="32" class="align-items-center">
        <Col span="6" v-show="formData.is_taxed">
          <FormItem :label="$t('quote.info.tax_rate')">
            <PercentInputNumber v-model="formData.tax_rate"></PercentInputNumber>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('quote.info.trade_type')" prop="trade_type" :rules="{ required: true, message: $t('quote.trade_type_is_required') }" required>
            <QuoteTradeTypeSelect v-model="formData.trade_type"></QuoteTradeTypeSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('quote.info.currency')" prop="currency">
            <CurrencySelect :value.sync="formData.currency" :disabled="hasCostEstimated || disabled"></CurrencySelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem class="hasDesc" :label="$t('exchange_rate')" prop="exchange_rate">
            <InputNumber v-model="formData.exchange_rate" :disabled="hasCostEstimated || disabled"></InputNumber>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" class="align-items-center">
        <Col span="12">
          <FormItem :label="$t('quote.info.payment_term')" prop="payment_term">
            <Input v-model="formData.payment_term"></Input>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('quote.info.payment_term_note')" prop="payment_term_note">
            <Input v-model="formData.payment_term_note"></Input>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" class="align-items-center">
        <Col span="12">
          <FormItem :label="$t('quote.info.owner')" prop="owner_id" :rules="{ required: true, message: $t('quote.owner_is_required') }">
            <PeopleSelect v-model="formData.owner_id" :placeholder="$t('quote.find_people_placeholder')" v-bind:people.sync="formData.owner"></PeopleSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('quote.info.status')" prop="status" :rules="{ required: true, message: $t('quote.status_is_required') }">
            <QuoteStatusSelect :quote-method="formData.method" v-model="formData.status"></QuoteStatusSelect>
          </FormItem>
        </Col>
        <Col span="6">
          <FormItem :label="$t('quote.info.expecting_completion_date')" prop="expecting_completion_date" :rules="{ required: true, message: $t('quote.expecting_completion_date_is_required') }">
            <DatePicker v-model="formData.expecting_completion_date"></DatePicker>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" class="align-items-center">
        <Col span="24">
          <FormItem :label="$t('quote.info.remark')">
            <Input v-model="formData.remark" type="textarea" :rows="8"></Input>
          </FormItem>
        </Col>
      </Row>
      <div class="text-align-right">
        <Button type="primary" :loading="loading" @click="handleSubmit('quoteForm')" size="large">{{ $t('save') }}</Button>
      </div>
    </Form>
  </div>
</template>

<script>
import CustomerInfo from '@/view/account/components/widgets/account-search-and-pick-widget.vue'
import { mapActions, mapGetters } from 'vuex'
import { PercentInputNumber, NsfPhaseSelect, ProcessOrderCategorySelect } from '@/components/nexpcb'
import { PeopleSelect } from '@/view/people/components'
import { QuoteMethodSelect, QuoteTradeTypeSelect, QuoteStatusSelect } from '../form-controls'
import QuoteTypeSingleProduct from './quote-type-single-product-form'
import CurrencySelect from '@/components/currency'
import moment from 'moment'

export default {
  name: 'QuoteForm',
  components: {
    CurrencySelect,
    QuoteTypeSingleProduct,
    PercentInputNumber,
    CustomerInfo,
    PeopleSelect,
    QuoteMethodSelect,
    QuoteTradeTypeSelect,
    QuoteStatusSelect,
    NsfPhaseSelect,
    ProcessOrderCategorySelect
  },
  props: {
    quote: {
      type: Object,
      required: false,
      default: () => {
        return {
          id: null,
          data: {}
        }
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasCostEstimated: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // define fields
      formData: {
        payment_term: '100%',
        owner: {},
        customer: {},
        data: {}
      },
      currencies: {}
    }
  },
  created () {
    // Load the necessary global configurations, such as, currency codes, terms and etc
    this.loadGlobalConfigs()
  },
  computed: {
    isEditing: function () {
      return this.quote && this.quote.id
    },
    hasHubSpotLink: function () {
      return _.findIndex(this.formData.external_object_relation, { external_name: 'hubspot' }) !== -1
    },
    ...mapGetters(['currencyExchangeRates'])
  },
  mounted () {
    this.init()
  },
  watch: {
    'quote': {
      deep: true,
      handler () {
        this.init()
      }
    },
    'formData.trade_type' (val) {
      if (val === 'export') {
        this.$set(this.formData, 'currency', 'USD')
      } else {
        this.$set(this.formData, 'currency', 'CNY')
      }
    },
    'formData.currency' (curr) {
      let currencyRateObj = this.currencyExchangeRates.find(x => x.currency_code === curr)
      if (currencyRateObj) {
        this.$set(this.formData, 'exchange_rate', currencyRateObj.avg_rate)
      }
    }
  },
  methods: {
    ...mapActions([
      'getConfigs',
      'getAccount',
      'getProject',
      'getCurrencyExchangeRates'
    ]),
    moment,
    init() {
      // Only load VAT (tax), when it is not set
      this.$nextTick(() => {
        let data = _.cloneDeep(this.quote)

        if (!this.quote.tax_rate) {
          data.tax_rate = this.$store.state.core.configs.tax_rate
        }

        if (this.$route.query.type) {
          data.type = this.$route.query.type
        }

        if (this.$route.query.customer_id) {
          data.customer_id = this.$route.query.customer_id
          this.getAccount(this.$route.query.customer_id).then((customer) => {
            this.$set(this.formData, 'customer', customer)
          })
        }

        if (!data.status) {
          data.status = 'created'
        }

        this.formData = data
        this.formData.exchange_rate = _.round(this.formData.exchange_rate, 4)
        this.formData.is_taxed = this.formData.is_taxed ? 1 : 0

        if (this.$refs.singleProductRef) {
          this.$refs.singleProductRef.setData(data.data)
        }
      })
    },
    loadGlobalConfigs () {
      this.getConfigs()
      this.getCurrencyExchangeRates()
    },
    checkBasecampProjectUrl (rule, value, callback) {
      // Validate basecamp url access
      if (this.formData.basecamp_todo_link) {
        let matches = this.formData.basecamp_todo_link.match(/\/buckets\/(\d+)\//)
        if (matches) {
          this.getProject(matches[1]).then((resp) => {
            if (resp.data.code) {
              callback(new Error(this.$t(`error_notice.${resp.data.code}`)))
            } else {
              callback()
            }
          })
        } else {
          callback(new Error(this.$t(`quote.basecamp_todo_link_not_correct`)))
        }
      } else {
        callback()
      }
    },
    handleSubmit(ref) {
      // a temporary fix due to date format validation error got from backend api.
      this.formData.expires_date = moment(this.formData.expires_date).format('YYYY-MM-DD')
      if (this.formData.expecting_completion_date) {
        this.formData.expecting_completion_date = moment(this.formData.expecting_completion_date).format('YYYY-MM-DD')
      }
      const data = _.cloneDeep(this.formData)
      this.$refs[ref].validate(valid => {
        if (valid) {
          if (this.$refs['singleProductRef']) {
            if (this.$refs['singleProductRef'].handleValidateForm()) {
              this.$emit('save-quote', data)
            }
          } else {
            this.$emit('save-quote', data)
          }
        }
      })
    },
    handleExpiresDateChange (date) {
      this.formData.expires_date = moment(date).format('YYYY-MM-DD')
    },
    handleQuoteTypeChanged (value) {
      this.$emit('on-quote-type-changed', value)
    }
  }
}
</script>

<style lang="less">
.ivu-tag {
  height: 32px;
  line-height: 32px;
  font-size: 120%;
  background: #fff;
}
.ivu-tag.new {
  border: dashed 1px #ccc;
}
.ivu-form-item .desc {
  font-size: 80%;
  line-height: 80%;
  white-space: nowrap;
  margin-top: 5px;
}
.ivue-form-item .hasDesc { margin-top: 20%; }
.ivu-form-item-content .text-align-right { float: right; }
</style>
